import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Target, Users, GitBranch, GraduationCap, MessageSquare, TrendingUp,
  ChevronRight, ChevronLeft, CheckCircle, AlertCircle, AlertTriangle,
  Loader2, Building2, Mail, DollarSign, BarChart3,
  Download, ArrowRight, Home, Award, Rocket, 
  Globe, ExternalLink, ChevronDown, ChevronUp, UserCheck,
  Shield, Zap, FileText, ArrowUpRight, Clock, Calendar, Flag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ReportSecurityNote } from '../components/ReportSecurityNote';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { submitMaturity } from '../utils/maturityJobs';
import { generateBPCMPptx } from '../utils/generateBPCMPptx';
import AGIInsightsTab from '../components/agi/AGIInsightsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping for dimensions
const dimensionIcons = {
  'Target': Target,
  'Users': Users,
  'UserCheck': UserCheck,
  'GitBranch': GitBranch,
  'GraduationCap': GraduationCap,
  'MessageSquare': MessageSquare,
  'TrendingUp': TrendingUp
};

// Industries
const industries = [
  'Aerospace & Defense', 'Agriculture', 'Automotive', 'Biotechnology', 'Chemicals',
  'Consumer Packaged Goods', 'Education', 'Energy', 'EPC (Engineering, Procurement & Construction)', 'Financial Services', 
  'Food Processing', 'Government', 'Healthcare', 'Insurance', 'Manufacturing',
  'Media & Entertainment', 'Nonprofit', 'Oil & Gas', 'Pharmaceuticals', 
  'Professional Services', 'Real Estate & Construction', 'Retail', 'Software',
  'Technology', 'Telecommunications', 'Travel & Logistics', 'Utilities'
];

// Company sizes
const companySizes = [
  { value: "1-50", label: "1-50 employees" },
  { value: "51-200", label: "51-200 employees" },
  { value: "201-1000", label: "201-1,000 employees" },
  { value: "1001-5000", label: "1,001-5,000 employees" },
  { value: "5001-10000", label: "5,001-10,000 employees" },
  { value: "10001+", label: "10,001+ employees" }
];

const BPCMAssessmentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentIdFromUrl = searchParams.get('id');
  
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [questionsData, setQuestionsData] = useState(null);
  const [responses, setResponses] = useState({});
  const [results, setResults] = useState(null);
  const [fullResults, setFullResults] = useState(null);
  const [error, setError] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [assessmentId, setAssessmentId] = useState(assessmentIdFromUrl || null);
  
  // URL scraping state
  const [scrapingUrl, setScrapingUrl] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);
  
  // Additional context state (simple textarea)
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatInput, setChatInput] = useState('');
  
  // Profile state
  const [profile, setProfile] = useState({
    company_name: '',
    company_url: '',
    industry: '',
    company_size: '',
    annual_revenue: '',
    respondent_name: '',
    respondent_role: '',
    respondent_email: ''
  });

  useEffect(() => {
    fetchQuestions();
  }, []);
  
  // Load assessment from URL if id parameter is present
  useEffect(() => {
    if (assessmentIdFromUrl) {
      loadAssessmentFromUrl(assessmentIdFromUrl);
    }
  }, [assessmentIdFromUrl]);
  
  const loadAssessmentFromUrl = async (id) => {
    setLoadingAssessment(true);
    try {
      const res = await axios.get(`${API}/bpcm/assessment/${id}`);
      if (res.data) {
        setAssessmentId(id);
        const assessment = res.data;
        // Check both code_verified and is_sample_preview flags
        const isSamplePreview = assessment.is_sample_preview === true;
        const isVerified = assessment.code_verified === true || !isSamplePreview;
        
        // Store email in profile for verification
        if (assessment.respondent_email) {
          setProfile(prev => ({ ...prev, respondent_email: assessment.respondent_email }));
        }
        
        setResults({
          success: true,
          assessment_id: id,
          id: id,
          respondent_email: assessment.respondent_email,
          requires_secret_code: !isVerified,
          is_sample_preview: isSamplePreview,
          sample_results: {
            company_name: assessment.company_name,
            industry: assessment.industry,
            overall_score: assessment.overall_score,
            maturity_level: assessment.maturity_level,
            industry_benchmark: assessment.industry_benchmark,
            dimension_scores: assessment.dimension_scores || [],
            total_dimensions_count: assessment.total_dimensions_count || assessment.dimension_scores?.length || 7,
            executive_summary_preview: (assessment.ai_analysis?.executive_summary || '').substring(0, 300) + '...'
          }
        });
        
        // Only set full results if verified and not a sample preview
        if (isVerified && !isSamplePreview) {
          setFullResults(assessment);
        }
        
        setStep(999);
      }
    } catch (err) {
      console.error('Error loading assessment:', err);
      setError('Assessment not found or has expired. Please start a new assessment.');
    } finally {
      setLoadingAssessment(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API}/bpcm/questions`);
      setQuestionsData(res.data);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
      setError('Failed to load assessment questions');
    }
  };

  const dimensions = questionsData?.dimensions || [];
  const totalSteps = dimensions.length + 2; // intro + profile + dimensions
  const currentDimensionIndex = step - 2;
  const isLastDimension = step === dimensions.length + 1;
  const progressPercent = ((step) / totalSteps) * 100;

  const validateProfile = () => {
    const required = ['company_name', 'industry', 'company_size', 'respondent_name', 'respondent_email'];
    for (const field of required) {
      if (!profile[field]) {
        setError('Please fill in all required fields');
        return false;
      }
    }
    if (!profile.respondent_email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    setError('');
    return true;
  };

  // Scrape company URL
  const scrapeCompanyUrl = async () => {
    if (!profile.company_url) return;
    
    setScrapingUrl(true);
    try {
      const formData = new FormData();
      formData.append('url', profile.company_url);
      formData.append('report_type', 'general');
      
      const res = await axios.post(`${API}/scrape-company-report`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      
      setScrapedData(res.data);
    } catch (err) {
      console.error('Error scraping URL:', err);
      setScrapedData({ success: false });
    } finally {
      setScrapingUrl(false);
    }
  };

  const handleNext = () => {
    if (step === 1 && !validateProfile()) return;
    setStep(prev => prev + 1);
    setError('');
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
    setError('');
  };

  const submitAssessment = async () => {
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('company_name', profile.company_name);
      formData.append('company_url', profile.company_url || '');
      formData.append('industry', profile.industry);
      formData.append('company_size', profile.company_size);
      formData.append('annual_revenue', profile.annual_revenue || '');
      formData.append('respondent_name', profile.respondent_name);
      formData.append('respondent_role', profile.respondent_role || '');
      formData.append('respondent_email', profile.respondent_email);
      formData.append('responses', JSON.stringify(responses));
      
      // Include additional context from textarea
      if (chatInput.trim()) {
        formData.append('additional_context', chatInput.trim());
      }
      
      if (scrapedData?.success && scrapedData?.extracted_info) {
        formData.append('scraped_data', JSON.stringify(scrapedData.extracted_info));
      }

      const res = await submitMaturity(`${API}/bpcm/assess`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000
      });

      setResults(res.data);
      setStep(totalSteps);
    } catch (err) {
      console.error('Assessment error:', err);
      setError(err.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!secretCode || secretCode.length !== 6) {
      setCodeError('Please enter a valid 6-digit code');
      return;
    }
    
    setVerifyingCode(true);
    setCodeError('');
    
    try {
      const formData = new FormData();
      formData.append('assessment_id', results.assessment_id || results.id);
      formData.append('secret_code', secretCode);
      // Use profile email, fall back to results email
      const email = profile.respondent_email || results.respondent_email;
      formData.append('email', email);
      
      const res = await axios.post(`${API}/bpcm/verify-code`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.verified) {
        setFullResults(res.data.full_results);
      }
    } catch (err) {
      // Handle different error response formats
      const detail = err.response?.data?.detail;
      let errorMessage = 'Invalid code';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message).join(', ') || errorMessage;
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || errorMessage;
      }
      
      setCodeError(errorMessage);
    } finally {
      setVerifyingCode(false);
    }
  };

  const getScoreStatus = (score, benchmarkAvg) => {
    if (!benchmarkAvg) benchmarkAvg = 2.6;
    const gap = score - benchmarkAvg;
    
    if (gap < -0.8) return { status: 'Critical Gap', color: 'text-red-600', bg: 'bg-red-100' };
    if (gap < -0.3) return { status: 'Below Average', color: 'text-amber-600', bg: 'bg-amber-100' };
    if (gap < 0.3) return { status: 'At Par', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { status: 'Above Average', color: 'text-green-600', bg: 'bg-green-100' };
  };

  // Loading state when loading assessment from URL
  if (loadingAssessment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-indigo-950 to-purple-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white">Loading Your Assessment...</h2>
          <p className="text-blue-200 mt-2">Please wait while we retrieve your results</p>
        </div>
      </div>
    );
  }

  // Render Intro
  if (step === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-indigo-950 to-purple-950">
        <header className="bg-black/20 backdrop-blur-sm border-b border-white/10 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <GitBranch className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-white">SAMANVAI</span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="gap-2 border-white/20 text-white hover:bg-white/10">
                <Home className="w-4 h-4" />
                Home
              </Button>
            </Link>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <GitBranch className="w-4 h-4" />
              Business Process Change Management Assessment
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              BPCM Maturity Assessment
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Evaluate your organization&apos;s change management capabilities across the 7 key levers 
              that drive business transformation success.
            </p>
          </div>

          {/* 7 Levers Grid */}
          <div className="grid md:grid-cols-4 gap-4 mb-12">
            {[
              { icon: Target, name: 'Management & Planning', color: 'from-blue-500 to-blue-600' },
              { icon: Users, name: 'Leadership Engagement', color: 'from-purple-500 to-purple-600' },
              { icon: UserCheck, name: 'Project Team Effectiveness', color: 'from-emerald-500 to-emerald-600' },
              { icon: GitBranch, name: 'Business Process Alignment', color: 'from-amber-500 to-amber-600' },
              { icon: GraduationCap, name: 'Learning & Capability', color: 'from-pink-500 to-pink-600' },
              { icon: MessageSquare, name: 'Communications', color: 'from-cyan-500 to-cyan-600' },
              { icon: TrendingUp, name: 'Realization & Sustainability', color: 'from-red-500 to-red-600' },
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-all">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center mb-3`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-medium text-white text-sm">{item.name}</h3>
                </div>
              );
            })}
            <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-xl p-4 flex flex-col justify-center items-center">
              <span className="text-3xl font-bold text-white">7</span>
              <span className="text-sm text-gray-300">Change Levers</span>
            </div>
          </div>

          {/* What You Get */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mb-12">
            <h3 className="text-xl font-semibold text-white mb-6 text-center">What You&apos;ll Receive</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: FileText, title: 'C-suite Ready Insight Report', desc: 'Executive summary, industry comparison, strategic roadmap' },
                { icon: BarChart3, title: 'ROI Analysis', desc: 'Cost-benefit analysis with business value realization' },
                { icon: Shield, title: 'Risk Mitigation', desc: 'Areas of concern with actionable recommendations' }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={idx} className="text-center">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-6 h-6 text-blue-400" />
                    </div>
                    <h4 className="font-medium text-white mb-1">{item.title}</h4>
                    <p className="text-sm text-gray-400">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={() => setStep(1)}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-lg px-8 py-6 rounded-xl shadow-lg"
              data-testid="btn-start"
            >
              Start Assessment
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-gray-400 text-sm mt-4">Takes approximately 10-15 minutes</p>
          </div>
        </div>
      </div>
    );
  }

  // Render Profile
  if (step === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <GitBranch className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">BPCM Assessment</span>
            </div>
            <div className="text-sm text-gray-500">Step 1 of {totalSteps}</div>
          </div>
          <Progress value={progressPercent} className="h-1" />
        </header>

        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Organization Profile</h2>
              <p className="text-gray-500">Tell us about your organization</p>
            </div>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                <Input
                  value={profile.company_name}
                  onChange={(e) => setProfile({...profile, company_name: e.target.value})}
                  placeholder="Enter company name"
                  data-testid="input-company"
                />
              </div>

              {/* Company URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Website / Annual Report URL
                  <span className="text-xs text-gray-500 ml-1">(Optional)</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      value={profile.company_url}
                      onChange={(e) => setProfile({...profile, company_url: e.target.value})}
                      placeholder="https://company.com/annual-report"
                      className="pr-10"
                      data-testid="input-url"
                    />
                    <Globe className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                  {profile.company_url && (
                    <Button variant="outline" onClick={scrapeCompanyUrl} disabled={scrapingUrl}>
                      {scrapingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
                {scrapedData?.success && (
                  <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Report analyzed successfully
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Industry *</label>
                <Select value={profile.industry} onValueChange={(v) => setProfile({...profile, industry: v})}>
                  <SelectTrigger data-testid="select-industry">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map((ind) => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Size *</label>
                  <Select value={profile.company_size} onValueChange={(v) => setProfile({...profile, company_size: v})}>
                    <SelectTrigger data-testid="select-size">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      {companySizes.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Annual Revenue</label>
                  <Select value={profile.annual_revenue} onValueChange={(v) => setProfile({...profile, annual_revenue: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select revenue" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="$0-10M">Under $10M</SelectItem>
                      <SelectItem value="$10M-100M">$10M - $100M</SelectItem>
                      <SelectItem value="$100M-500M">$100M - $500M</SelectItem>
                      <SelectItem value="$500M-1B">$500M - $1B</SelectItem>
                      <SelectItem value="$1B+">Over $1B</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <hr className="my-4" />
              <p className="text-sm text-gray-500">Your Information</p>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                  <Input
                    value={profile.respondent_name}
                    onChange={(e) => setProfile({...profile, respondent_name: e.target.value})}
                    placeholder="John Doe"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Role</label>
                  <Input
                    value={profile.respondent_role}
                    onChange={(e) => setProfile({...profile, respondent_role: e.target.value})}
                    placeholder="Change Manager"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <Input
                  type="email"
                  value={profile.respondent_email}
                  onChange={(e) => setProfile({...profile, respondent_email: e.target.value})}
                  placeholder="john@company.com"
                  data-testid="input-email"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="flex justify-between pt-4">
                <Button variant="outline" onClick={() => setStep(0)}>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button onClick={handleNext} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-continue">
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Questions
  if (step >= 2 && step < totalSteps && !results) {
    const currentDimension = dimensions[currentDimensionIndex];
    if (!currentDimension) return null;

    const DimensionIcon = dimensionIcons[currentDimension.icon] || Target;

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{background: currentDimension.color}}>
                <DimensionIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{currentDimension.name}</h2>
                <p className="text-xs text-gray-500">Dimension {currentDimensionIndex + 1} of {dimensions.length}</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">Step {step} of {totalSteps - 1}</div>
          </div>
          <Progress value={progressPercent} className="h-1" />
        </header>

        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <p className="text-gray-600 mb-8">{currentDimension.description}</p>

            <div className="space-y-8">
              {currentDimension.questions.map((q, qIdx) => (
                <div key={q.id} className="border-b border-gray-100 pb-6 last:border-0">
                  <p className="font-medium text-gray-900 mb-4">{qIdx + 1}. {q.text}</p>
                  <div className="space-y-2">
                    {q.options.map((opt) => (
                      <label
                        key={opt.value}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          responses[q.id] === opt.value 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name={q.id}
                          value={opt.value}
                          checked={responses[q.id] === opt.value}
                          onChange={() => setResponses({...responses, [q.id]: opt.value})}
                          className="mt-1"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-700">Level {opt.value}:</span>
                          <span className="text-sm text-gray-600 ml-1">{opt.label}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Context section on last dimension - Simple textarea */}
            {isLastDimension && (
              <div className="mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowChatbot(!showChatbot)}
                  className="w-full justify-between"
                >
                  <span className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Additional Context (Optional)
                  </span>
                  {showChatbot ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>

                {showChatbot && (
                  <div className="mt-4 bg-gray-50 rounded-xl p-4">
                    <p className="text-sm text-gray-600 mb-4">
                      Share any additional details that could help us provide more accurate recommendations:
                    </p>
                    <ul className="text-xs text-gray-500 mb-4 space-y-1">
                      <li>• Current change management challenges or pain points</li>
                      <li>• Ongoing or upcoming transformation initiatives</li>
                      <li>• Specific areas you&apos;d like us to focus on</li>
                      <li>• Recent change projects (successes or failures)</li>
                      <li>• Organizational culture considerations</li>
                    </ul>
                    <Textarea
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      placeholder="E.g., We're currently implementing a new ERP system across 5 countries. Our main challenge is getting middle management buy-in. We've had mixed results with previous change initiatives..."
                      className="min-h-[150px] resize-none"
                      data-testid="textarea-additional-context"
                    />
                    <p className="text-xs text-gray-400 mt-2">
                      This information will be used to personalize your assessment report.
                    </p>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="flex justify-between mt-8">
              <Button variant="outline" onClick={handleBack} data-testid="btn-back">
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              
              {isLastDimension ? (
                <Button 
                  onClick={submitAssessment} 
                  disabled={loading}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                  data-testid="btn-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Submit Assessment
                      <Rocket className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              ) : (
                <Button onClick={handleNext} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-next">
                  Next Dimension
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Results
  if (results) {
    const benchmark = results.industry_benchmark || { avg: 2.6, top: 4.2 };
    const maturityLevel = results.maturity_level || { name: 'Developing', color: 'yellow' };

    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                <GitBranch className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">BPCM Assessment Results</span>
            </div>
            {fullResults && (
              <Button
                onClick={() => generateBPCMPptx(results, fullResults)}
                className="bg-blue-600 hover:bg-blue-700 gap-2"
                data-testid="btn-download-pptx"
              >
                <Download className="w-4 h-4" />
                Download PowerPoint
              </Button>
            )}
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Score Summary */}
          <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-2xl p-8 text-white mb-8">
            <div className="grid md:grid-cols-4 gap-6 items-center">
              <div className="text-center">
                <div className="text-6xl font-bold">{results.overall_score}</div>
                <div className="text-blue-200">out of 5.0</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-blue-200 mb-1">Maturity Level</div>
                <div className="text-2xl font-semibold">{maturityLevel.name}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-blue-200 mb-1">Industry Average</div>
                <div className="text-2xl font-semibold">{benchmark.avg}</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-blue-200 mb-1">Top Performers</div>
                <div className="text-2xl font-semibold">{benchmark.top}</div>
              </div>
            </div>
          </div>

          {/* Dimension Scores */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">7 BPCM Levers Analysis</h3>
            <div className="space-y-4">
              {results.dimension_scores?.map((dim) => {
                const status = getScoreStatus(dim.score, benchmark.avg);
                const DimIcon = dimensionIcons[dim.icon] || Target;
                
                // Get improvement suggestions from fullResults if available
                const dimAnalysis = fullResults?.ai_analysis?.dimension_analysis?.find(
                  d => d.dimension_id === dim.dimension_id || d.dimension === dim.dimension_name
                );
                const improvements = dimAnalysis?.improvement_suggestions || [
                  'Establish formal processes and documentation',
                  'Increase stakeholder engagement and communication',
                  'Implement measurement and tracking mechanisms'
                ];

                return (
                  <div key={dim.dimension_id} className={`p-4 rounded-xl border ${
                    status.color === 'text-red-600' ? 'bg-red-50 border-red-200' : 
                    status.color === 'text-amber-600' ? 'bg-amber-50 border-amber-200' : 
                    'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center gap-4 mb-3">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{background: dim.color}}>
                        <DimIcon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-gray-900">{dim.dimension_name}</span>
                          <span className={`text-xs px-2 py-1 rounded-full ${status.bg} ${status.color} font-medium`}>
                            {status.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Progress value={(dim.score / 5) * 100} className="flex-1 h-2" />
                          <span className="text-sm font-bold text-gray-700">{dim.score.toFixed(1)}/5</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* How to Improve */}
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <h4 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-1">
                        <TrendingUp className="w-4 h-4" />
                        How to Improve
                      </h4>
                      <ul className="space-y-1">
                        {improvements.slice(0, 3).map((item, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Code Verification */}
          {!fullResults ? (
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-2xl p-8 mb-8">
              <div className="max-w-md mx-auto text-center">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Unlock Full Report</h3>
                <p className="text-gray-600 mb-6">
                  A secret code has been sent to <strong>{profile.respondent_email || results?.respondent_email || 'your email'}</strong>. 
                  Enter it below to access your complete BPCM report.
                </p>
                <div className="flex gap-3 justify-center">
                  <Input
                    value={secretCode}
                    onChange={(e) => setSecretCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit code"
                    className="w-40 text-center text-lg font-mono tracking-wider"
                    data-testid="input-secret-code"
                  />
                  <Button
                    onClick={verifyCode}
                    disabled={verifyingCode || secretCode.length !== 6}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="btn-verify-code"
                  >
                    {verifyingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                  </Button>
                </div>
                {codeError && <p className="text-red-500 text-sm mt-2">{codeError}</p>}
              </div>
            </div>
          ) : (
            /* Full Results */
            <div className="space-y-8">
              {/* Executive Summary */}
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Award className="w-6 h-6 text-blue-600" />
                  Executive Summary
                </h3>
                <div className="prose prose-blue max-w-none">
                  {(() => {
                    const execSummary = fullResults.ai_analysis?.executive_summary;
                    const score = fullResults.overall_score;
                    const level = fullResults.maturity_level?.name || 'Developing';
                    const industry = fullResults.industry;
                    const benchAvg = fullResults.industry_benchmark?.avg || 2.6;
                    const benchTop = fullResults.industry_benchmark?.top || 4.2;
                    
                    // Find strongest and weakest dimensions
                    const dims = fullResults.dimension_scores || [];
                    const sortedDims = [...dims].sort((a, b) => b.score - a.score);
                    const strongest = sortedDims.slice(0, 2);
                    const weakest = sortedDims.slice(-2).reverse();
                    
                    if (typeof execSummary === 'string' && execSummary.length > 100) {
                      return execSummary.split('\n').filter(p => p.trim()).map((para, i) => (
                        <p key={i} className="text-gray-700 mb-3 leading-relaxed">{para}</p>
                      ));
                    }
                    
                    // Generate default executive summary
                    const gapStatus = score >= benchAvg ? 'above' : 'below';
                    const gap = Math.abs(score - benchAvg).toFixed(2);
                    
                    return (
                      <>
                        <p className="text-gray-700 mb-3 leading-relaxed">
                          <strong>{fullResults.company_name}</strong> has achieved an overall BPCM Maturity Score of <strong>{score?.toFixed(1)}/5.0</strong>, 
                          placing the organization at the <strong>&quot;{level}&quot;</strong> maturity level. This score is {gap} points {gapStatus} the 
                          {industry} industry average of {benchAvg} and {(benchTop - score).toFixed(1)} points below top performers ({benchTop}).
                        </p>
                        <p className="text-gray-700 mb-3 leading-relaxed">
                          <strong>Key Strengths:</strong> The assessment reveals strongest performance in 
                          {strongest.map((d, i) => (
                            <span key={i}>{i === 0 ? ' ' : ' and '}<em>{d.dimension_name}</em> ({d.score?.toFixed(1)})</span>
                          ))}. These areas demonstrate established processes and stakeholder engagement.
                        </p>
                        <p className="text-gray-700 mb-3 leading-relaxed">
                          <strong>Priority Improvement Areas:</strong> The most critical gaps exist in 
                          {weakest.map((d, i) => (
                            <span key={i}>{i === 0 ? ' ' : ' and '}<em>{d.dimension_name}</em> ({d.score?.toFixed(1)})</span>
                          ))}. Addressing these areas will significantly improve overall change management effectiveness.
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                          The 12-month strategic roadmap provides a phased approach to advancing BPCM maturity, 
                          with focus on governance establishment, capability building, and sustainable change practices.
                        </p>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Areas of Concern */}
              {fullResults.ai_analysis?.areas_of_concern && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    Areas of Concern
                  </h3>
                  <div className="space-y-4">
                    {fullResults.ai_analysis.areas_of_concern.critical?.length > 0 && (
                      <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg">
                        <h4 className="font-semibold text-red-800 mb-2">🔴 Critical - Immediate Action Required</h4>
                        <ul className="space-y-1">
                          {fullResults.ai_analysis.areas_of_concern.critical.map((item, i) => (
                            <li key={i} className="text-red-700 text-sm">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {fullResults.ai_analysis.areas_of_concern.high_priority?.length > 0 && (
                      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg">
                        <h4 className="font-semibold text-amber-800 mb-2">🟠 High Priority - Action within 3 months</h4>
                        <ul className="space-y-1">
                          {fullResults.ai_analysis.areas_of_concern.high_priority.map((item, i) => (
                            <li key={i} className="text-amber-700 text-sm">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {fullResults.ai_analysis.areas_of_concern.medium_priority?.length > 0 && (
                      <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
                        <h4 className="font-semibold text-yellow-800 mb-2">🟡 Medium Priority - Action within 6 months</h4>
                        <ul className="space-y-1">
                          {fullResults.ai_analysis.areas_of_concern.medium_priority.map((item, i) => (
                            <li key={i} className="text-yellow-700 text-sm">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 12-Month Roadmap */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Rocket className="w-5 h-5 text-blue-600" />
                  12-Month Strategic Roadmap
                </h3>
                <div className="grid md:grid-cols-4 gap-4">
                  {(() => {
                    const roadmap = fullResults.ai_analysis?.strategic_roadmap || {};
                    const defaultPhases = [
                      { key: 'phase_1', name: 'Foundation', months: 'Months 1-3', initiatives: ['Change governance setup', 'Stakeholder mapping', 'Communication plan'], investment: '$30K - $60K' },
                      { key: 'phase_2', name: 'Build Capability', months: 'Months 4-6', initiatives: ['Leadership alignment', 'Training infrastructure', 'Change agent network'], investment: '$50K - $100K' },
                      { key: 'phase_3', name: 'Scale & Integrate', months: 'Months 7-9', initiatives: ['Process alignment', 'Culture transformation', 'Benefits tracking'], investment: '$60K - $120K' },
                      { key: 'phase_4', name: 'Optimize & Sustain', months: 'Months 10-12', initiatives: ['Knowledge transfer', 'Continuous improvement', 'BAU transition'], investment: '$40K - $80K' }
                    ];
                    const colors = ['bg-blue-500', 'bg-purple-500', 'bg-indigo-500', 'bg-violet-500'];
                    
                    return defaultPhases.map((phase, i) => {
                      const phaseData = roadmap[phase.key] || {};
                      const phaseName = phaseData.name || phase.name;
                      const initiatives = phaseData.key_initiatives || phaseData.objectives || phase.initiatives;
                      const investment = phaseData.investment_range || phase.investment;
                      
                      return (
                        <div key={phase.key} className="border border-gray-200 rounded-xl overflow-hidden">
                          <div className={`${colors[i]} text-white p-3 text-center`}>
                            <div className="text-xs opacity-80">Q{i + 1}</div>
                            <div className="font-bold text-sm">{phaseName}</div>
                            <div className="text-xs opacity-80">{phase.months}</div>
                          </div>
                          <div className="p-3 bg-gray-50">
                            <ul className="space-y-1">
                              {(Array.isArray(initiatives) ? initiatives : []).slice(0, 3).map((item, j) => (
                                <li key={j} className="text-xs text-gray-600 flex items-start gap-1">
                                  <CheckCircle className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                                  <span>{typeof item === 'string' ? item : item.name || 'Initiative'}</span>
                                </li>
                              ))}
                            </ul>
                            <div className="mt-2 text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded text-center">
                              {investment}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              {/* ROI Analysis */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  ROI Analysis & Business Value
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: 'Total Investment', value: fullResults.ai_analysis?.roi_analysis?.total_investment || 'Not available', color: 'border-blue-200' },
                    { label: 'Expected ROI', value: fullResults.ai_analysis?.roi_analysis?.expected_roi || 'Not available', color: 'border-green-200' },
                    { label: 'Payback Period', value: '12-18 months', color: 'border-purple-200' },
                    { label: 'Risk Reduction', value: '40-60%', color: 'border-amber-200' }
                  ].map((item, i) => (
                    <div key={i} className={`bg-white rounded-xl p-4 border ${item.color}`}>
                      <div className="text-xs text-gray-500 mb-1">{item.label}</div>
                      <div className="text-lg font-bold text-gray-900">{item.value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Risk Mitigation */}
              {fullResults.ai_analysis?.risk_mitigation?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Risk Mitigation Strategies
                  </h3>
                  <div className="space-y-3">
                    {fullResults.ai_analysis.risk_mitigation.slice(0, 5).map((item, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          item.impact === 'High' ? 'bg-red-100 text-red-700' : 
                          item.impact === 'Medium' ? 'bg-amber-100 text-amber-700' : 
                          'bg-green-100 text-green-700'
                        }`}>
                          {item.impact || 'Medium'}
                        </span>
                        <div>
                          <h4 className="font-medium text-gray-900">{item.risk}</h4>
                          <p className="text-sm text-gray-600 mt-1">{item.mitigation_strategy}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Use Cases */}
              {fullResults.ai_analysis?.use_cases?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Zap className="w-5 h-5 text-purple-600" />
                    Recommended Use Cases
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {fullResults.ai_analysis.use_cases.slice(0, 4).map((uc, i) => (
                      <div key={i} className="bg-purple-50 rounded-xl p-4">
                        <h4 className="font-semibold text-purple-900">{uc.title}</h4>
                        <p className="text-sm text-purple-700 mt-1">{uc.description}</p>
                        {uc.expected_impact && (
                          <p className="text-xs text-purple-600 mt-2">Impact: {uc.expected_impact}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Case Studies */}
              {fullResults.case_studies?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-indigo-600" />
                    Relevant Case Studies
                  </h3>
                  <div className="space-y-4">
                    {fullResults.case_studies.slice(0, 2).map((cs, i) => (
                      <div key={i} className="bg-indigo-50 rounded-xl p-5">
                        <h4 className="font-semibold text-indigo-900 mb-2">{cs.title}</h4>
                        <p className="text-sm text-indigo-700 mb-3">{cs.challenge}</p>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <h5 className="text-xs font-semibold text-indigo-800 mb-1">Approach</h5>
                            <ul className="space-y-1">
                              {cs.approach?.slice(0, 3).map((a, j) => (
                                <li key={j} className="text-xs text-indigo-600">• {a}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h5 className="text-xs font-semibold text-indigo-800 mb-1">Results</h5>
                            <ul className="space-y-1">
                              {cs.results?.slice(0, 3).map((r, j) => (
                                <li key={j} className="text-xs text-indigo-600">• {r}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                        {cs.roi && (
                          <div className="mt-3 text-sm font-medium text-indigo-800">
                            ROI: {cs.roi}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Level Up Section - Journey to Next BPCM Maturity Level */}
              {fullResults?.ai_analysis?.level_progression && (
                <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl shadow-lg p-8 border-2 border-blue-200 mt-8" data-testid="level-up-section">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                      <ArrowUpRight className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Level Up: Journey to Next Change Management Maturity</h2>
                      <p className="text-gray-600">What it takes to advance your change management capabilities</p>
                    </div>
                  </div>

                  {/* Current to Next Level Visualization */}
                  <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                      <div className="text-center flex-1">
                        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                          <span className="text-3xl font-bold text-white">
                            {fullResults.ai_analysis.level_progression.current_level?.level_number || Math.floor(fullResults.overall_score)}
                          </span>
                        </div>
                        <div className="font-bold text-gray-900">Current Level</div>
                        <div className="text-sm text-gray-500">
                          {fullResults.ai_analysis.level_progression.current_level?.level_name || fullResults.maturity_level?.name}
                        </div>
                      </div>
                      <div className="flex-1 px-4">
                        <div className="relative">
                          <div className="h-2 bg-gradient-to-r from-amber-400 via-blue-400 to-indigo-500 rounded-full" />
                          <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-center">
                            <div className="flex items-center gap-1 bg-blue-100 px-3 py-1 rounded-full">
                              <Clock className="w-3 h-3 text-blue-600" />
                              <span className="text-xs font-medium text-blue-700">
                                {fullResults.ai_analysis.level_progression.progression_requirements?.time_estimate || '6-9 months'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="text-center flex-1">
                        <div className="w-20 h-20 mx-auto bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                          <span className="text-3xl font-bold text-white">
                            {fullResults.ai_analysis.level_progression.next_level?.level_number || Math.min(Math.floor(fullResults.overall_score) + 1, 5)}
                          </span>
                        </div>
                        <div className="font-bold text-gray-900">Target Level</div>
                        <div className="text-sm text-blue-600 font-medium">
                          {fullResults.ai_analysis.level_progression.next_level?.level_name || 'Next Level'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Resource Requirements Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-blue-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Clock className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="font-bold text-gray-900">Time</div>
                      </div>
                      <div className="text-2xl font-bold text-blue-600">
                        {fullResults.ai_analysis.level_progression.progression_requirements?.time_estimate || '6-9 months'}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-blue-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <Users className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="font-bold text-gray-900">Manpower</div>
                      </div>
                      <div className="text-lg font-bold text-purple-600">
                        {fullResults.ai_analysis.level_progression.progression_requirements?.manpower_resources?.internal_fte || 'Not available'}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-blue-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="font-bold text-gray-900">Investment</div>
                      </div>
                      <div className="text-lg font-bold text-indigo-600">
                        {fullResults.ai_analysis.level_progression.progression_requirements?.investment_required?.total_range || 'Not available'}
                      </div>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm border border-blue-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="font-bold text-gray-900">Expected Value</div>
                      </div>
                      <div className="text-lg font-bold text-amber-600">
                        {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.quantified_value || 'Not available'}
                      </div>
                    </div>
                  </div>

                  {/* Milestones */}
                  {fullResults.ai_analysis.level_progression.milestones?.length > 0 && (
                    <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                      <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                        <Flag className="w-5 h-5 text-blue-600" />
                        Key Milestones to Next Level
                      </h3>
                      <div className="relative">
                        <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-blue-400 to-indigo-500" />
                        <div className="space-y-6">
                          {fullResults.ai_analysis.level_progression.milestones.map((milestone, idx) => (
                            <div key={idx} className="relative flex gap-4">
                              <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 z-10 bg-gradient-to-br from-blue-500 to-indigo-600 shadow-lg">
                                <span className="text-white font-bold">{milestone.milestone_number || idx + 1}</span>
                              </div>
                              <div className="flex-1 bg-gradient-to-r from-blue-50 to-transparent rounded-lg p-4 border border-blue-100">
                                <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-bold text-gray-900">{milestone.name}</h4>
                                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {milestone.target_month}
                                  </span>
                                </div>
                                {milestone.key_deliverables?.length > 0 && (
                                  <div className="text-sm text-gray-600 mb-2">
                                    <strong>Deliverables:</strong>
                                    <ul className="list-disc list-inside ml-2 mt-1">
                                      {milestone.key_deliverables.slice(0, 3).map((d, i) => (
                                        <li key={i} className="text-gray-600">{d}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                                {milestone.success_criteria && (
                                  <div className="text-xs text-blue-600 font-medium">
                                    ✓ Success: {milestone.success_criteria}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Business Benefits at Next Level */}
                  <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                    <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                      Business Benefits at Next Change Management Level
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-4 border border-emerald-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm font-medium text-gray-600">Change Success Rate</span>
                        </div>
                        <div className="text-lg font-bold text-emerald-700">
                          {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.efficiency_gains || 'Not available'}
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-4 border border-blue-200">
                        <div className="flex items-center gap-2 mb-2">
                          <TrendingUp className="w-4 h-4 text-blue-600" />
                          <span className="text-sm font-medium text-gray-600">Adoption Speed</span>
                        </div>
                        <div className="text-lg font-bold text-blue-700">
                          {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.revenue_impact || 'Not available'}
                        </div>
                      </div>
                      <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg p-4 border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="w-4 h-4 text-amber-600" />
                          <span className="text-sm font-medium text-gray-600">Resistance Reduction</span>
                        </div>
                        <div className="text-lg font-bold text-amber-700">
                          {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.risk_reduction || 'Not available'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Key Challenges & Quick Wins */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {fullResults.ai_analysis.level_progression.key_challenges?.length > 0 && (
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-100">
                        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                          Key Challenges
                        </h3>
                        <ul className="space-y-2">
                          {fullResults.ai_analysis.level_progression.key_challenges.map((challenge, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                              <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="text-amber-700 text-xs font-bold">{idx + 1}</span>
                              </div>
                              {challenge}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {fullResults.ai_analysis.level_progression.quick_wins_to_accelerate?.length > 0 && (
                      <div className="bg-white rounded-xl p-6 shadow-sm border border-blue-100">
                        <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                          <Rocket className="w-5 h-5 text-blue-600" />
                          Quick Wins to Accelerate
                        </h3>
                        <ul className="space-y-2">
                          {fullResults.ai_analysis.level_progression.quick_wins_to_accelerate.map((win, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                              {win}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* AGI Insights */}
          <div className="mt-8" data-testid="agi-insights-section">
            <AGIInsightsTab
              assessmentType="Business Process & Change Management"
              assessmentId={results?.assessment_id || results?.id}
            />
          </div>

          <ReportSecurityNote />

          {/* Actions */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
              <Home className="w-4 h-4" />
              Back to Home
            </Button>
            {fullResults && (
              <Button
                onClick={() => generateBPCMPptx(results, fullResults)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 gap-2"
                data-testid="btn-download-pptx-bottom"
              >
                <Download className="w-4 h-4" />
                Download Presentation
              </Button>
            )}
            <Button
              onClick={() => {
                setStep(0);
                setResults(null);
                setFullResults(null);
                setResponses({});
                setChatInput('');
                setProfile({
                  company_name: '',
                  company_url: '',
                  industry: '',
                  company_size: '',
                  annual_revenue: '',
                  respondent_name: '',
                  respondent_role: '',
                  respondent_email: ''
                });
              }}
              className="bg-gray-800 hover:bg-gray-900 gap-2"
            >
              <ArrowRight className="w-4 h-4" />
              Start New Assessment
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BPCMAssessmentPage;
